import { Divider, Icons, Stack, Typography } from '@healthinal/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useGetAllPatientsBasicsSuspense } from '../../api/created/patients.ts';
import { useGetHistorySuspense } from '../../api/generated/history-controller/history-controller.ts';
import { GetPatientsBasicsType, HistoryDto, HistoryDtoHistoryType, PatientBasicsDto } from '../../api/generated/model';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import { useCustomVirtualizer } from '../../hooks/useCustomVirtualizer.ts';
import { historyPatientSearchTextAtom } from '../../state/historyPatientSearchAtoms.ts';
import {
  historyFilterAtom,
  historyRangeFilterAtom,
  historySelectedPatientFilterAtom,
} from '../../state/historySearchAtoms.ts';
import { HISTORY_ENTRY_HEIGHT } from '../../theme.ts';
import { formatDateForInput, formatDateYear } from '../../utils/date.ts';
import { filterHistory } from '../../utils/historySearch/historyFilter.ts';
import { PatientSideListNav } from '../components/PatientSideListNav.tsx';
import { HistoryDirectlyObservedTherapyStarted } from './components/HistoryDirectlyObservedTherapyStarted.tsx';
import { HistoryHeaderFilter } from './components/HistoryHeaderFilter.tsx';
import { HistoryHospitalizationOrIsolationEnded } from './components/HistoryHospitalizationOrIsolationEnded.tsx';
import { HistoryHospitalizationOrIsolationStarted } from './components/HistoryHospitalizationOrIsolationStarted.tsx';
import { HistoryJournal } from './components/HistoryJournal.tsx';
import { HistoryLabReportCreated } from './components/HistoryLabReportCreated.tsx';
import { HistoryMedicationChange } from './components/HistoryMedicationChange.tsx';
import { HistoryPatientCreated } from './components/HistoryPatientCreated.tsx';
import { HistoryResidencyChanged } from './components/HistoryResidencyChanged.tsx';
import { HistoryTherapyFinished } from './components/HistoryTherapyFinished.tsx';

export const Route = createFileRoute('/history/')({
  component: History,
});

function History() {
  const [range] = useAtom(historyRangeFilterAtom);

  const { data: patients } = useGetAllPatientsBasicsSuspense({ type: GetPatientsBasicsType.ALL, assignedTo: 'ALL' });

  const { data: history } = useGetHistorySuspense({
    from: formatDateForInput(range.from),
    until: formatDateForInput(range.until),
  });

  const contentSectionRef = useRef(null);

  const [patientSearchText, setPatientSearchText] = useAtom(historyPatientSearchTextAtom);
  const [selectedPatient, setSelectedPatient] = useAtom(historySelectedPatientFilterAtom);
  const [selectedHistoryFilter] = useAtom(historyFilterAtom);

  const [filteredHistory, setFilteredHistory] = useState(history);

  useEffect(() => {
    setFilteredHistory(filterHistory(history, selectedHistoryFilter, selectedPatient));
  }, [selectedPatient, selectedHistoryFilter, history]);

  const { virtualItems, totalSize, paddingTop, paddingBottom, measureElement } = useCustomVirtualizer({
    count: filteredHistory.length,
    cellHeight: HISTORY_ENTRY_HEIGHT,
    parentRef: contentSectionRef,
    overscan: 10,
  });

  return (
    <Stack direction={'row'} gap={3}>
      <PatientSideListNav
        patients={patients}
        searchText={patientSearchText}
        setSearchText={setPatientSearchText}
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
      />
      <ContentSection flex={4} ref={contentSectionRef}>
        <StickyContainer gap={2}>
          {selectedPatient ? (
            <Typography
              level={'h3'}
              endDecorator={
                <Typography fontWeight={400} textColor={'text.tertiary'}>
                  {formatDateYear(selectedPatient.birthDate)}
                </Typography>
              }>
              {selectedPatient.caseNumber} {selectedPatient.firstName}
            </Typography>
          ) : (
            <Typography level={'h3'}>Alle Patienten</Typography>
          )}
          <Stack>
            <Typography level={'h4'}>Verlauf</Typography>
            <HistoryHeaderFilter />
          </Stack>
          <MotionDivider contentSectionRef={contentSectionRef} />
        </StickyContainer>
        <ErrorBoundary>
          {!filteredHistory.length && (
            <Stack alignItems={'center'} flex={1} paddingTop={5}>
              <Typography level={'title-lg'}>Keine Verlauf vorhanden</Typography>
            </Stack>
          )}

          <Stack height={`${totalSize}px`} paddingTop={`${paddingTop}px`} paddingBottom={`${paddingBottom}px`}>
            {virtualItems.map((virtualRow) => {
              const historyEntry = filteredHistory[virtualRow.index];
              const patient = patients.find((patient) => patient.id === historyEntry.patientId);
              if (!patient) return;
              return (
                <Stack
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={measureElement}
                  direction={'row'}
                  gap={2}>
                  <Stack alignItems={'center'}>
                    {getIconForHistoryType(historyEntry.historyType)}
                    <Stack flex={1} direction={'row'}>
                      <Divider orientation={'vertical'} />
                    </Stack>
                  </Stack>
                  <Stack paddingBottom={3} flex={1}>
                    {getComponentForHistory(historyEntry, patient)}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </ErrorBoundary>
      </ContentSection>
    </Stack>
  );
}

function getIconForHistoryType(historyType: HistoryDtoHistoryType) {
  switch (historyType) {
    case HistoryDtoHistoryType.JOURNAL_ENTRY:
      return <Icons.Notes />;
    case HistoryDtoHistoryType.PATIENT_CREATED:
      return <Icons.PersonAdd />;
    case HistoryDtoHistoryType.THERAPY_FINISHED:
      return <Icons.MedicationLiquid />;
    case HistoryDtoHistoryType.LAB_REPORT_CREATED:
      return <Icons.Science />;
    case HistoryDtoHistoryType.THERAPY_MEDICATION_CHANGE:
      return <Icons.MedicationLiquid />;
    case HistoryDtoHistoryType.RESIDENCY_CHANGED:
      return <Icons.Hub />;
    case HistoryDtoHistoryType.DOT_STARTED:
      return <Icons.SupervisorAccount />;
    case HistoryDtoHistoryType.ISOLATION_STARTED:
    case HistoryDtoHistoryType.ISOLATION_ENDED:
      return <Icons.SafetyDivider size={'lg'} />;
    case HistoryDtoHistoryType.HOSPITALIZATION_STARTED:
    case HistoryDtoHistoryType.HOSPITALIZATION_ENDED:
      return <Icons.MedicalServices />;
  }
}

function getComponentForHistory(history: HistoryDto, patient: PatientBasicsDto) {
  switch (history.historyType) {
    case HistoryDtoHistoryType.JOURNAL_ENTRY:
      return <HistoryJournal history={history} patient={patient} />;
    case HistoryDtoHistoryType.PATIENT_CREATED:
      return <HistoryPatientCreated history={history} patient={patient} />;
    case HistoryDtoHistoryType.THERAPY_FINISHED:
      return <HistoryTherapyFinished history={history} patient={patient} />;
    case HistoryDtoHistoryType.LAB_REPORT_CREATED:
      return <HistoryLabReportCreated history={history} patient={patient} />;
    case HistoryDtoHistoryType.THERAPY_MEDICATION_CHANGE:
      return <HistoryMedicationChange history={history} patient={patient} />;
    case HistoryDtoHistoryType.RESIDENCY_CHANGED:
      return <HistoryResidencyChanged history={history} patient={patient} />;
    case HistoryDtoHistoryType.DOT_STARTED:
      return <HistoryDirectlyObservedTherapyStarted history={history} patient={patient} />;
    case HistoryDtoHistoryType.ISOLATION_STARTED:
    case HistoryDtoHistoryType.HOSPITALIZATION_STARTED:
      return <HistoryHospitalizationOrIsolationStarted history={history} patient={patient} />;
    case HistoryDtoHistoryType.ISOLATION_ENDED:
    case HistoryDtoHistoryType.HOSPITALIZATION_ENDED:
      return <HistoryHospitalizationOrIsolationEnded history={history} patient={patient} />;
  }
}
